import React, { useState, useRef } from 'react';
import { useTierListStore } from '../store/tierListStore';
import { Plus, Edit2, Trash2, Upload } from 'lucide-react';
import { CreateTierListModal } from './CreateTierListModal';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';

export const Home: React.FC = () => {
  const { state, dispatch } = useTierListStore();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteModalData, setDeleteModalData] = useState<{ id: string; title: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createNewTierList = (title: string) => {
    const newList = dispatch({
      type: 'CREATE_TIER_LIST',
      payload: {
        title,
        tiers: [],
        unassignedItems: [],
      },
    });

    // Navigate to the new tier list
    window.location.hash = newList.id;
  };

  const handleStartEditing = (e: React.MouseEvent, id: string, currentTitle: string) => {
    e.stopPropagation();
    setEditingId(id);
    setEditTitle(currentTitle);
  };

  const handleSaveTitle = (id: string) => {
    if (editTitle.trim()) {
      dispatch({
        type: 'UPDATE_TIER_LIST',
        payload: {
          id,
          title: editTitle.trim(),
        },
      });
    }
    setEditingId(null);
  };

  const handleDeleteTierList = (e: React.MouseEvent, id: string, title: string) => {
    e.stopPropagation();
    setDeleteModalData({ id, title });
  };

  const confirmDelete = () => {
    if (deleteModalData) {
      dispatch({
        type: 'DELETE_TIER_LIST',
        payload: { id: deleteModalData.id },
      });
      setDeleteModalData(null);
    }
  };

  const handleCardClick = (id: string) => {
    window.location.hash = id;
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const importedData = JSON.parse(event.target?.result as string);
        const newList = dispatch({
          type: 'CREATE_TIER_LIST',
          payload: {
            title: importedData.title,
            tiers: importedData.tiers,
            unassignedItems: importedData.unassignedItems,
          },
        });
        window.location.hash = newList.id;
      } catch (error) {
        console.error('Failed to import tier list:', error);
        alert('Failed to import tier list. Please check the file format.');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset the input
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Tier Lists</h1>
        <div className="flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImport}
            accept=".json"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <Upload size={20} /> Import
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <Plus size={20} /> New Tier List
          </button>
        </div>
      </div>

      {state.tierLists.length === 0 ? (
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            No tier lists yet
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Create your first tier list to get started!
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {state.tierLists.map((tierList) => (
            <div
              key={tierList.id}
              onClick={() => handleCardClick(tierList.id)}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-center justify-between gap-4 mb-2">
                {editingId === tierList.id ? (
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onBlur={() => handleSaveTitle(tierList.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveTitle(tierList.id);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 px-2 py-1 text-lg font-semibold border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    autoFocus
                  />
                ) : (
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                    {tierList.title}
                  </h3>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => handleStartEditing(e, tierList.id, tierList.title)}
                    className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={(e) => handleDeleteTierList(e, tierList.id, tierList.title)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-full transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <span>
                  {tierList.tiers.length} tiers ·{' '}
                  {tierList.tiers.reduce(
                    (total, tier) => total + tier.items.length,
                    0
                  ) + tierList.unassignedItems.length}{' '}
                  items
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateTierListModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={createNewTierList}
      />

      <DeleteConfirmationModal
        isOpen={deleteModalData !== null}
        onClose={() => setDeleteModalData(null)}
        onConfirm={confirmDelete}
        title={deleteModalData?.title || ''}
      />
    </div>
  );
};